import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { DinoLibrarian } from '@/components/DinoLibrarian';
import { FocusTimer } from '@/components/FocusTimer';
import { FocusCelebration } from '@/components/FocusCelebration';
import { BookSelector } from '@/components/BookSelector';
import { SessionHistory } from '@/components/SessionHistory';
import { FocusStats } from '@/components/FocusStats';
import { FocusStreakDisplay } from '@/components/FocusStreakDisplay';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { CoverImage } from '@/components/CoverImage';
import { ArrowLeft, Timer as TimerIcon } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useFocusPreferences } from '@/hooks/useFocusPreferences';
import dinoFocusImage from '@/assets/dino-focus.png';

type SessionState = 'idle' | 'setup' | 'active' | 'completed';

interface SelectedBook {
  id: string;
  title: string;
  authors: string[];
  cover_url: string | null;
}

interface UserBookDetails {
  user_book_id: string;
  current_page: number;
  total_pages: number;
}

export default function Focus() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { preferences, updatePreferences } = useFocusPreferences(user?.id);
  const [state, setState] = useState<SessionState>('idle');
  const [isPaused, setIsPaused] = useState(false);
  const [selectedBook, setSelectedBook] = useState<SelectedBook | null>(null);
  const [userBookDetails, setUserBookDetails] = useState<UserBookDetails | null>(null);
  const [showBookSelector, setShowBookSelector] = useState(false);
  const [duration, setDuration] = useState(preferences.pomodoro_mode_enabled ? preferences.pomodoro_focus_duration : 30);
  const [customDuration, setCustomDuration] = useState('');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [streakNotification, setStreakNotification] = useState<{ current: number; continued: boolean } | null>(null);

  // Calculate Dino pose based on progress
  const getDinoPose = () => {
    if (state !== 'active') return 'reading';
    const progress = (elapsedSeconds / (duration * 60)) * 100;
    if (progress < 20) return 'shelving';
    if (progress < 40) return 'organizing';
    if (progress < 60) return 'reading';
    if (progress < 80) return 'stamping';
    return 'tidying';
  };

  // Start session mutation
  const startSessionMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // If book is selected and from TBR, move it to Currently Reading
      if (selectedBook) {
        const { data: existingUserBook } = await supabase
          .from('user_books')
          .select('id, shelf, started_at')
          .eq('user_id', user.id)
          .eq('book_id', selectedBook.id)
          .maybeSingle();

        if (existingUserBook) {
          // Book exists in user library
          if (existingUserBook.shelf === 'to-read') {
            // Move from TBR to Currently Reading
            await supabase
              .from('user_books')
              .update({
                shelf: 'reading',
                started_at: existingUserBook.started_at || new Date().toISOString().split('T')[0],
              })
              .eq('id', existingUserBook.id);

            toast({ title: 'Book moved to Currently Reading! 📖' });
          }
        } else {
          // Book not in user library yet, add it to Currently Reading
          await supabase
            .from('user_books')
            .insert({
              user_id: user.id,
              book_id: selectedBook.id,
              shelf: 'reading',
              started_at: new Date().toISOString().split('T')[0],
            });

          toast({ title: 'Book added to Currently Reading! 📖' });
        }
      }

      const now = new Date();
      const { data, error } = await supabase
        .from('focus_sessions')
        .insert({
          user_id: user.id,
          book_id: selectedBook?.id || null,
          duration_minutes: duration,
          started_at: now.toISOString(),
        })
        .select()
        .single();

      if (error) throw error;
      return { sessionId: data.id, startTime: now };
    },
    onSuccess: ({ sessionId, startTime }) => {
      setSessionId(sessionId);
      setStartTime(startTime);
      setState('active');
      setIsPaused(false);
      toast({ title: 'Focus session started! 📚' });
    },
    onError: (error) => {
      console.error('Failed to start session:', error);
      toast({ 
        title: 'Failed to start session', 
        description: error instanceof Error ? error.message : 'Please try again',
        variant: 'destructive' 
      });
    },
  });

  // Complete session mutation
  const completeSessionMutation = useMutation({
    mutationFn: async ({ wasCompleted, actualSeconds }: { wasCompleted: boolean; actualSeconds: number }) => {
      if (!sessionId) return null;

      const { error } = await supabase
        .from('focus_sessions')
        .update({
          actual_duration_seconds: actualSeconds,
          was_completed: wasCompleted,
          completed_at: new Date().toISOString(),
        })
        .eq('id', sessionId);

      if (error) throw error;

      // If session completed with a book, fetch user_book details
      if (wasCompleted && selectedBook) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: userBook } = await supabase
            .from('user_books')
            .select('id, current_page, total_pages')
            .eq('user_id', user.id)
            .eq('book_id', selectedBook.id)
            .maybeSingle();

          if (userBook) {
            return {
              user_book_id: userBook.id,
              current_page: userBook.current_page || 0,
              total_pages: userBook.total_pages || 0,
            };
          }
        }
      }

      return null;
    },
    onSuccess: (details) => {
      queryClient.invalidateQueries({ queryKey: ['focus-sessions'] });
      queryClient.invalidateQueries({ queryKey: ['focus-stats'] });
      queryClient.invalidateQueries({ queryKey: ['focus-streak'] });
      queryClient.invalidateQueries({ queryKey: ['user-books-reading'] });
      queryClient.invalidateQueries({ queryKey: ['user-books-tbr'] });
      
      if (details) {
        setUserBookDetails(details);
      }
    },
  });

  const handleStartSetup = () => {
    setState('setup');
  };

  const handleStartSession = () => {
    startSessionMutation.mutate();
  };

  const handleComplete = async (finalElapsedSeconds: number) => {
    setElapsedSeconds(finalElapsedSeconds);
    
    // Update focus streak
    if (user?.id) {
      try {
        const { data, error } = await supabase.rpc('update_focus_streak', {
          p_user_id: user.id
        });
        
        if (!error && data && data.length > 0) {
          const streakResult = data[0];
          if (streakResult.streak_continued) {
            setStreakNotification({
              current: streakResult.current_streak,
              continued: true
            });
            toast({
              title: `🔥 ${streakResult.current_streak} Day Streak!`,
              description: `+${streakResult.xp_awarded} XP for maintaining your streak!`,
            });
          } else if (streakResult.current_streak === 1) {
            setStreakNotification({
              current: 1,
              continued: false
            });
          }
        }
      } catch (error) {
        console.error('Error updating focus streak:', error);
      }
    }
    
    completeSessionMutation.mutate({ wasCompleted: true, actualSeconds: finalElapsedSeconds });
    setState('completed');
  };

  const handleMilestoneReached = async (milestone: number) => {
    if (!sessionId || !user?.id) return;

    try {
      // Record milestone
      await supabase.from('focus_session_milestones').insert({
        session_id: sessionId,
        milestone_percent: milestone,
        xp_awarded: 5
      });

      // Award XP
      await supabase.rpc('award_xp', {
        p_user_id: user.id,
        p_amount: 5,
        p_reason: `Focus milestone ${milestone}%`,
        p_activity_type: 'focus_milestone'
      });
    } catch (error) {
      console.error('Error recording milestone:', error);
    }
  };

  const handleStop = (finalElapsedSeconds: number) => {
    setElapsedSeconds(finalElapsedSeconds);
    completeSessionMutation.mutate({ wasCompleted: false, actualSeconds: finalElapsedSeconds });
    setState('idle');
    setSelectedBook(null);
    setUserBookDetails(null);
    setElapsedSeconds(0);
    toast({ title: 'Session stopped' });
  };

  const handleCelebrationClose = () => {
    setState('idle');
    setSelectedBook(null);
    setUserBookDetails(null);
    setElapsedSeconds(0);
  };

  const handleProgressUpdated = () => {
    queryClient.invalidateQueries({ queryKey: ['user-books-reading'] });
    queryClient.invalidateQueries({ queryKey: ['focus-stats'] });
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8">
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              className="rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-foreground font-heading">
                Focus Mode
              </h1>
              <p className="text-muted-foreground">Stay focused with Dino Librarian</p>
            </div>
          </div>
          <TimerIcon className="w-8 h-8 text-primary" />
        </div>

        {/* Idle State */}
        {state === 'idle' && (
          <>
            <Card className="p-8 text-center space-y-6 dino-gradient">
              <img src={dinoFocusImage} alt="Dino reading" className="mx-auto w-36 h-36 object-contain" />
              <div className="space-y-3">
                <h2 className="text-2xl font-bold text-foreground font-heading">
                  Ready to Focus?
                </h2>
                <p className="text-muted-foreground">
                  Start a focused reading session with your friendly librarian companion
                </p>
              </div>
              <Button onClick={handleStartSetup} size="lg" className="btn-pill">
                Start Focus Session
              </Button>
            </Card>

            {/* Focus Streak */}
            {user && <FocusStreakDisplay userId={user.id} />}

            <FocusStats />

            <div>
              <h3 className="text-xl font-semibold mb-4 font-heading">Recent Sessions</h3>
              <SessionHistory />
            </div>
          </>
        )}

        {/* Setup State */}
        {state === 'setup' && (
          <Card className="p-8 space-y-6">
            <h2 className="text-2xl font-bold text-foreground font-heading">
              Setup Your Session
            </h2>

            {/* Book selection */}
            <div className="space-y-3">
              <Label>What are you reading?</Label>
              {selectedBook ? (
                <div className="flex items-center gap-4 p-4 rounded-lg bg-muted">
                  <CoverImage
                    bookId={selectedBook.id}
                    title={selectedBook.title}
                    author={selectedBook.authors?.[0]}
                    coverUrl={selectedBook.cover_url}
                    size="xs"
                  />
                  <div className="flex-1">
                    <p className="font-semibold">{selectedBook.title}</p>
                    <p className="text-sm text-muted-foreground">
                      {selectedBook.authors?.join(', ')}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowBookSelector(true)}
                  >
                    Change
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => setShowBookSelector(true)}
                  className="w-full"
                >
                  Select a Book (Optional)
                </Button>
              )}
            </div>

            {/* Pomodoro Mode Toggle */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-muted">
              <div className="space-y-1">
                <Label>Pomodoro Mode</Label>
                <p className="text-xs text-muted-foreground">
                  Take 5-minute breaks between 25-minute sessions
                </p>
              </div>
              <Switch
                checked={preferences.pomodoro_mode_enabled}
                onCheckedChange={(checked) => {
                  updatePreferences({ pomodoro_mode_enabled: checked });
                  if (checked) {
                    setDuration(preferences.pomodoro_focus_duration);
                  }
                }}
              />
            </div>

            {/* Duration selection */}
            <div className="space-y-3">
              <Label>Focus Duration</Label>
              <RadioGroup 
                value={duration.toString()} 
                onValueChange={(v) => {
                  setDuration(parseInt(v));
                  setCustomDuration('');
                }}
                disabled={preferences.pomodoro_mode_enabled}
              >
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {[15, 30, 45, 60].map((mins) => (
                    <label
                      key={mins}
                      className="flex items-center space-x-2 p-4 rounded-lg border cursor-pointer hover:bg-muted transition-colors disabled:opacity-50"
                    >
                      <RadioGroupItem value={mins.toString()} disabled={preferences.pomodoro_mode_enabled} />
                      <span className="font-medium">{mins} min</span>
                    </label>
                  ))}
                  
                  {/* Custom duration input */}
                  <div className="p-4 rounded-lg border">
                    <Input
                      type="number"
                      min="1"
                      max="180"
                      placeholder="Custom"
                      value={customDuration}
                      onChange={(e) => {
                        const value = e.target.value;
                        setCustomDuration(value);
                        if (value && parseInt(value) > 0) {
                          setDuration(parseInt(value));
                        }
                      }}
                      disabled={preferences.pomodoro_mode_enabled}
                      className="h-auto border-0 p-0 text-center font-medium focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                  </div>
                </div>
              </RadioGroup>
            </div>

            <div className="flex gap-3">
              <Button 
                onClick={() => setState('idle')} 
                variant="outline" 
                className="flex-1 btn-pill"
                disabled={startSessionMutation.isPending}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleStartSession} 
                className="flex-1 btn-pill"
                disabled={startSessionMutation.isPending}
              >
                {startSessionMutation.isPending ? 'Starting...' : 'Start Session'}
              </Button>
            </div>
          </Card>
        )}

        {/* Active State */}
        {state === 'active' && (
          <Card className="p-8 space-y-8">
            {isPaused && (
              <div className="text-center p-4 bg-muted rounded-lg">
                <p className="text-muted-foreground">
                  Session paused. Click Resume when you're ready to continue.
                </p>
              </div>
            )}

            <FocusTimer
              durationMinutes={duration}
              onComplete={handleComplete}
              onStop={handleStop}
              isPaused={isPaused}
              onPauseToggle={() => setIsPaused(!isPaused)}
              pose={getDinoPose()}
              sessionId={sessionId}
              onMilestoneReached={handleMilestoneReached}
            />

            {selectedBook && (
              <div className="text-center">
                <p className="text-sm text-muted-foreground">Currently reading</p>
                <p className="font-semibold text-foreground">{selectedBook.title}</p>
              </div>
            )}
          </Card>
        )}

        {/* Completed State */}
        {state === 'completed' && (
          <FocusCelebration
            onClose={handleCelebrationClose}
            selectedBook={selectedBook}
            userBookDetails={userBookDetails}
            onProgressUpdated={handleProgressUpdated}
          />
        )}
      </div>

      {/* Book Selector Dialog */}
      <BookSelector
        open={showBookSelector}
        onClose={() => setShowBookSelector(false)}
        onSelect={setSelectedBook}
      />
    </div>
  );
}
